/**
 * P4-03: LogFilters —— 右栏日志过滤器勾选列表。
 *
 * 取代旧 `js/ui.js:914-930` / `998-1014` 的 `renderFilters` / `_createFilter`：
 * - 通过 `useGameTick` 读取 `game.console.filters`；
 * - 仅渲染 `unlocked` 的过滤器，按 key 字母序排列（对齐旧实现）；
 * - 点击复选框直接修改 `filter.enabled`（与旧 `_createFilter` 一致）；
 * - 没有任何过滤器解锁时返回 null，控制方据此隐藏整块区域。
 */
import { useCallback } from "react";
import { useGameTick } from "../../bridge/useGameTick";
import type { GamePage } from "../../bridge/GameBridge";

export interface LogFilter {
  title: string;
  enabled: boolean;
  unlocked: boolean;
  defaultUnlocked?: boolean;
}

export interface LogFiltersSnapshot {
  filters: Record<string, LogFilter>;
  hasUnlocked: boolean;
}

function getFiltersSnapshot(game: GamePage | null): LogFiltersSnapshot | null {
  if (!game) {
    return null;
  }
  const filters = game.console?.filters as Record<string, LogFilter> | undefined;
  if (!filters) {
    return null;
  }
  const hasUnlocked = Object.values(filters).some((f) => f.unlocked);
  return { filters, hasUnlocked };
}

function snapEq(a: LogFiltersSnapshot | null, b: LogFiltersSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.hasUnlocked !== b.hasUnlocked) {
    return false;
  }
  const keysA = Object.keys(a.filters);
  const keysB = Object.keys(b.filters);
  if (keysA.length !== keysB.length) {
    return false;
  }
  for (const key of keysA) {
    const fa = a.filters[key];
    const fb = b.filters[key];
    if (!fa || !fb) {
      return false;
    }
    if (
      fa.title !== fb.title ||
      fa.enabled !== fb.enabled ||
      fa.unlocked !== fb.unlocked
    ) {
      return false;
    }
  }
  return true;
}

interface LogFiltersProps {
  visible?: boolean;
}

export default function LogFilters({ visible = true }: LogFiltersProps) {
  const selector = useCallback(
    (game: GamePage | null) => getFiltersSnapshot(game),
    [],
  );
  const snapshot = useGameTick(selector, snapEq);

  if (!snapshot || !snapshot.hasUnlocked || !visible) {
    return null;
  }

  const sortedKeys = Object.keys(snapshot.filters).sort();

  return (
    <div id="logFilters" className="log-filters">
      {sortedKeys.map((fId) => {
        const filter = snapshot.filters[fId];
        if (!filter.unlocked) {
          return null;
        }
        const id = `filter-${fId}`;
        return (
          <div key={fId} className="log-filter-item">
            <input
              id={id}
              type="checkbox"
              checked={filter.enabled}
              onChange={() => {
                filter.enabled = !filter.enabled;
              }}
            />
            <label
              htmlFor={id}
              dangerouslySetInnerHTML={{ __html: filter.title }}
            />
          </div>
        );
      })}
    </div>
  );
}
